import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { GoogleMap, Marker, Polygon } from '@react-google-maps/api';
import { FaMap, FaUpload, FaDatabase } from 'react-icons/fa';
import { mockInfrastructureDataSources } from '../../../data/mockScenarioData';
import { API_ENDPOINTS } from '../../../config/utilityApiConfig';
import { getSelectedUtility, setSelectedUtility as persistSelectedUtility } from '../../../utils/utilitySelection';
import { useGoogleMaps } from '../../../contexts/GoogleMapsContext';
import { createBoundsFromGeoJson, geoJsonToGoogleMapsPath } from '../../../utils/geoJsonUtils';

const mapContainerStyle = {
  width: '100%',
  height: '384px',
};

const mapOptions = {
  disableDefaultUI: false,
  zoomControl: true,
  streetViewControl: false,
  mapTypeControl: false,
  fullscreenControl: true,
  styles: [
    {
      featureType: 'poi',
      elementType: 'labels',
      stylers: [{ visibility: 'off' }],
    },
  ],
};

const DEFAULT_CENTER = { lat: 36.7783, lng: -119.4179 };

function getChargingStationType(station) {
  const dcFastCount = Number(station?.ev_dc_fast_count || 0);
  const level2Count = Number(station?.ev_level2_evse_num || 0);
  if (dcFastCount > 0) return 'dc_fast';
  if (level2Count > 0) return 'level2';
  return 'other';
}

function getChargingMarkerIcon(googleMaps, stationType) {
  if (!googleMaps?.Size || !googleMaps?.Point) return undefined;
  const isDcFast = stationType === 'dc_fast';
  const fillColor = isDcFast ? '#2563eb' : '#16a34a';
  const glyph = isDcFast ? 'DC' : 'L2';
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="30" height="42" viewBox="0 0 30 42">
      <path d="M15 1C8 1 2.5 6.5 2.5 13.5C2.5 23.5 15 40.5 15 40.5C15 40.5 27.5 23.5 27.5 13.5C27.5 6.5 22 1 15 1Z" fill="${fillColor}" stroke="#FFFFFF" stroke-width="2"/>
      <text x="15" y="18" text-anchor="middle" font-family="Arial,sans-serif" font-size="8" font-weight="700" fill="#FFFFFF">${glyph}</text>
    </svg>
  `.trim();
  return {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
    scaledSize: new googleMaps.Size(30, 42),
    anchor: new googleMaps.Point(15, 40),
  };
}

const InfrastructureTab = ({ data, updateData, region }) => {
  const { isLoaded, loadError } = useGoogleMaps();
  const mapRef = useRef(null);
  const [selectedUtility, setSelectedUtilityState] = useState(() => getSelectedUtility());
  const [boundaryGeometry, setBoundaryGeometry] = useState(null);
  const [stations, setStations] = useState([]);
  const [loadingMapData, setLoadingMapData] = useState(false);
  const [mapError, setMapError] = useState('');

  const [layers, setLayers] = useState({
    capacity: {
      source: data.capacityMapSource || 'default',
      visible: true,
      dataSourceId: 'utility-hosting-capacity',
      upload: null
    },
    charging: {
      source: data.chargingInfraSource || 'default',
      visible: true,
      dataSourceId: 'afdc-stations',
      upload: null
    },
    pois: {
      source: data.poisSource || 'default',
      visible: true,
      dataSourceId: 'osm-pois',
      upload: null
    }
  });

  const handleLayerSourceChange = (layerKey, source) => {
    const newLayers = {
      ...layers,
      [layerKey]: { ...layers[layerKey], source }
    };
    setLayers(newLayers);
    updateData({
      capacityMapSource: newLayers.capacity.source,
      chargingInfraSource: newLayers.charging.source,
      poisSource: newLayers.pois.source
    });
  };

  const handleLayerVisibilityToggle = (layerKey) => {
    setLayers({
      ...layers,
      [layerKey]: { ...layers[layerKey], visible: !layers[layerKey].visible }
    });
  };

  useEffect(() => {
    setSelectedUtilityState(getSelectedUtility());
  }, []);

  const utilityName = selectedUtility?.utilityName || '';
  const utilityState = selectedUtility?.stateCode || region || '';

  const boundaryPaths = useMemo(() => {
    if (!boundaryGeometry?.coordinates) return [];
    const paths = geoJsonToGoogleMapsPath(boundaryGeometry.coordinates, boundaryGeometry.type || 'MultiPolygon');
    if (!Array.isArray(paths) || paths.length === 0) return [];
    return boundaryGeometry.type === 'Polygon' ? [paths] : paths;
  }, [boundaryGeometry]);

  const fitMapToBoundary = useCallback(() => {
    if (!mapRef.current || !boundaryGeometry?.coordinates || !window?.google?.maps) return;
    const bounds = createBoundsFromGeoJson(boundaryGeometry.coordinates, boundaryGeometry.type || 'MultiPolygon');
    if (bounds) mapRef.current.fitBounds(bounds);
  }, [boundaryGeometry]);

  useEffect(() => {
    fitMapToBoundary();
  }, [fitMapToBoundary]);

  useEffect(() => {
    let cancelled = false;
    const fetchMapData = async () => {
      const token = localStorage.getItem('authToken');
      if (!token) {
        setMapError('Missing auth token. Please log in again.');
        return;
      }
      if (!utilityName) {
        setMapError('Select a utility first to load infrastructure map layers.');
        return;
      }

      setLoadingMapData(true);
      setMapError('');

      try {
        const headers = { Authorization: `Token ${token}` };
        const boundaryRes = await axios.get(API_ENDPOINTS.getStopChainUtilityBoundary, {
          headers,
          params: { utility_name: utilityName },
        });
        const mapContext = boundaryRes.data?.map_context || {};
        const boundary = mapContext?.utility_boundary_geometry || null;
        const territoryIds = Array.isArray(mapContext?.utility_territory_ids) ? mapContext.utility_territory_ids : [];
        const numericTerritoryIds = territoryIds.map((id) => Number(id)).filter((id) => Number.isFinite(id));

        if (numericTerritoryIds.length > 0) {
          persistSelectedUtility({
            stateCode: selectedUtility?.stateCode || region || '',
            utilityName,
            utilityTerritoryId: numericTerritoryIds[0],
          });
        }

        let stationRows = [];
        if (numericTerritoryIds.length > 0) {
          const stationResponses = await Promise.all(
            numericTerritoryIds.slice(0, 10).map((territoryId) =>
              axios.get(API_ENDPOINTS.getEvChargingStations, {
                headers,
                params: { utility_territory_id: territoryId, limit: 1000 },
              })
            )
          );
          stationRows = stationResponses.flatMap((res) =>
            Array.isArray(res.data?.stations) ? res.data.stations : []
          );
        } else if (utilityState) {
          const stationRes = await axios.get(API_ENDPOINTS.getEvChargingStations, {
            headers,
            params: { state: utilityState, limit: 1000 },
          });
          stationRows = Array.isArray(stationRes.data?.stations) ? stationRes.data.stations : [];
          setMapError('Utility boundary was not resolved. Showing state-level charging stations.');
        } else {
          setMapError('Unable to resolve utility boundary and no state fallback is available.');
        }

        if (cancelled) return;
        const seen = new Set();
        const deduped = stationRows.filter((s) => {
          const key = `${s?.id || ''}|${s?.external_id || ''}|${s?.latitude || ''}|${s?.longitude || ''}`;
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });
        const validStations = deduped.filter(
          (s) => Number.isFinite(Number(s?.latitude)) && Number.isFinite(Number(s?.longitude))
        );
        setBoundaryGeometry(boundary);
        setStations(validStations);
      } catch (err) {
        if (cancelled) return;
        setBoundaryGeometry(null);
        setStations([]);
        setMapError(err.response?.data?.error || err.message || 'Failed to load infrastructure map data.');
      } finally {
        if (!cancelled) setLoadingMapData(false);
      }
    };

    fetchMapData();
    return () => {
      cancelled = true;
    };
  }, [region, selectedUtility?.stateCode, utilityName, utilityState]);

  const layerConfig = [
    {
      key: 'capacity',
      name: 'Existing Capacity Map',
      icon: <FaMap className="text-gray-600" size={20} />,
      color: 'gray',
      description: 'Grid hosting capacity and substation availability',
      defaultSource: {
        name: 'Utility Hosting Capacity Data',
        description: 'Internal utility grid capacity maps',
        format: 'GeoJSON',
        lastUpdated: '2024-01-15'
      }
    },
    {
      key: 'charging',
      name: 'Existing Charging Infrastructure',
      icon: <FaMap className="text-gray-600" size={20} />,
      color: 'gray',
      description: 'Public and private EV charging stations',
      defaultSource: mockInfrastructureDataSources.find(s => s.id === 'afdc-stations')
    },
    {
      key: 'pois',
      name: 'Points of Interest (POIs)',
      icon: <FaMap className="text-gray-600" size={20} />,
      color: 'gray',
      description: 'Commercial centers, transit hubs, residential areas',
      defaultSource: mockInfrastructureDataSources.find(s => s.id === 'osm-pois')
    }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
        <div className="mb-3">
          <h3 className="text-xl font-bold text-gray-800 font-ui">Infrastructure Data</h3>
        </div>
        <p className="text-sm text-gray-700">
          Configure data layers for existing grid capacity, charging infrastructure, and points of interest. 
          Use default data sources or upload your own geospatial data.
        </p>
      </div>

      {/* Infrastructure Map */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="bg-gray-800 text-white p-4 flex items-center justify-between">
          <h4 className="font-semibold font-ui">Infrastructure Map - {region}</h4>
          <div className="flex gap-2">
            {layerConfig.map(layer => (
              <button
                key={layer.key}
                onClick={() => handleLayerVisibilityToggle(layer.key)}
                className={`px-3 py-1 rounded text-sm font-semibold transition-colors ${
                  layers[layer.key].visible
                    ? 'bg-gray-700 hover:bg-gray-800'
                    : 'bg-gray-500 hover:bg-gray-600'
                }`}
              >
                {layer.name}
              </button>
            ))}
          </div>
        </div>
        <div className="p-4 space-y-3">
          {loadError && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              Google Maps failed to load.
            </div>
          )}
          {mapError && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
              {mapError}
            </div>
          )}
          <div className="rounded-lg overflow-hidden border border-gray-200 relative">
            {!isLoaded ? (
              <div className="h-96 bg-gray-100 flex items-center justify-center text-gray-500">
                Loading map...
              </div>
            ) : (
              <GoogleMap
                mapContainerStyle={mapContainerStyle}
                center={DEFAULT_CENTER}
                zoom={7}
                options={mapOptions}
                onLoad={(map) => {
                  mapRef.current = map;
                  fitMapToBoundary();
                }}
              >
                {boundaryPaths.map((path, idx) => (
                  <Polygon
                    key={`infra-boundary-${idx}`}
                    paths={path}
                    options={{
                      strokeColor: '#1d4ed8',
                      strokeOpacity: 0.95,
                      strokeWeight: 2,
                      fillColor: '#60a5fa',
                      fillOpacity: 0.18,
                      clickable: false,
                      zIndex: 1,
                    }}
                  />
                ))}
                {layers.charging.visible &&
                  stations.map((station) => {
                    const lat = Number(station?.latitude);
                    const lng = Number(station?.longitude);
                    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
                    const stationType = getChargingStationType(station);
                    return (
                      <Marker
                        key={`charging-${station.id}`}
                        position={{ lat, lng }}
                        title={station.station_name || 'Charging station'}
                        icon={getChargingMarkerIcon(window?.google?.maps, stationType)}
                      />
                    );
                  })}
              </GoogleMap>
            )}
            {loadingMapData && (
              <div className="absolute inset-0 bg-white/70 flex items-center justify-center text-sm text-gray-600">
                Loading boundary and charging infrastructure...
              </div>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2 text-xs text-gray-600">
            <span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-blue-800">
              {boundaryGeometry ? 'Utility boundary loaded' : 'Boundary unavailable'}
            </span>
            <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-green-800">
              {stations.length} charging station{stations.length === 1 ? '' : 's'}
            </span>
            {!layers.charging.visible && <span>Enable "Existing Charging Infrastructure" to view markers.</span>}
          </div>
        </div>
      </div>

      {/* Layer Configuration */}
      <div className="space-y-4">
        {layerConfig.map(layer => {
          const currentLayer = layers[layer.key];
          
          return (
            <div
              key={layer.key}
              className={`bg-white rounded-lg border-2 ${
                currentLayer.visible ? 'border-gray-300' : 'border-gray-200'
              } overflow-hidden transition-all`}
            >
              <div className={`p-4 ${
                currentLayer.visible ? 'bg-gray-50' : 'bg-gray-50'
              }`}>
                <div className="flex items-start justify-between">
                  <div className="flex items-start flex-1">
                    <div className="mr-3 mt-1">{layer.icon}</div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="text-lg font-bold text-gray-800 font-ui">
                          {layer.name}
                        </h4>
                        <label className="flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={currentLayer.visible}
                            onChange={() => handleLayerVisibilityToggle(layer.key)}
                            className="mr-2"
                          />
                          <span className="text-sm text-gray-600">Show on map</span>
                        </label>
                      </div>
                      <p className="text-sm text-gray-600">{layer.description}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-4 space-y-4">
                {/* Data Source Selection */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Data Source
                  </label>
                  <div className="space-y-2">
                    <label className="flex items-start p-3 border-2 rounded-lg cursor-pointer transition-all hover:border-gray-300">
                      <input
                        type="radio"
                        name={`${layer.key}-source`}
                        value="default"
                        checked={currentLayer.source === 'default'}
                        onChange={(e) => handleLayerSourceChange(layer.key, e.target.value)}
                        className="mt-1 mr-3"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <FaDatabase className="text-gray-600" size={14} />
                          <p className="font-semibold text-gray-800">Use Default Data Source</p>
                        </div>
                        {layer.defaultSource && (
                          <div className="text-xs text-gray-600 ml-5 space-y-1">
                            <p><span className="font-semibold">Source:</span> {layer.defaultSource.name || layer.defaultSource.source}</p>
                            <p><span className="font-semibold">Description:</span> {layer.defaultSource.description}</p>
                            <p><span className="font-semibold">Format:</span> {layer.defaultSource.format}</p>
                            {layer.defaultSource.lastUpdated && (
                              <p><span className="font-semibold">Last Updated:</span> {layer.defaultSource.lastUpdated}</p>
                            )}
                            {layer.defaultSource.url && (
                              <p>
                                <a href={layer.defaultSource.url} target="_blank" rel="noopener noreferrer" className="text-gray-700 hover:underline">
                                  View source documentation →
                                </a>
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    </label>

                    <label className="flex items-start p-3 border-2 rounded-lg cursor-pointer transition-all hover:border-gray-300">
                      <input
                        type="radio"
                        name={`${layer.key}-source`}
                        value="upload"
                        checked={currentLayer.source === 'upload'}
                        onChange={(e) => handleLayerSourceChange(layer.key, e.target.value)}
                        className="mt-1 mr-3"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <FaUpload className="text-gray-600" size={14} />
                          <p className="font-semibold text-gray-800">Upload My Own Data</p>
                        </div>
                        {currentLayer.source === 'upload' && (
                          <div className="ml-5">
                            <input
                              type="file"
                              accept=".geojson,.json,.csv,.zip"
                              className="block w-full text-sm text-gray-600 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200"
                            />
                            <p className="text-xs text-gray-500 mt-2">
                              Accepted formats: GeoJSON, CSV (with lat/lon), Shapefile (zipped)
                            </p>
                          </div>
                        )}
                      </div>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Coverage Info */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <p className="text-sm text-gray-700">
          <span className="font-semibold">Region Coverage:</span> All data will be automatically clipped 
          to the {region} utility service territory boundary.
        </p>
      </div>
    </div>
  );
};

export default InfrastructureTab;
